import {
  BACKGAMMON_BAR_WIDTH,
  BACKGAMMON_OVERLAY_U,
  BACKGAMMON_POINT_WIDTH,
} from '@strategy4kids/board-3d';
import type { BackgammonEndpoint, BlotRisk } from '@strategy4kids/game-engine';
import type { BackgammonPosition } from '@strategy4kids/game-engine';
import { useMemo } from 'react';

/**
 * Literal testids for the 24 point hitboxes. The testid gate is a STATIC
 * extractor — it cannot resolve `bg-point-${p}`, so the ids must exist in
 * full somewhere under a src dir (see the `square-${sq}` precedent). This
 * array is that somewhere, and the tap layer renders from it, so the literal
 * and the DOM can never drift apart: there is exactly one list.
 */
export const BG_POINT_TESTIDS = [
  'bg-point-1',
  'bg-point-2',
  'bg-point-3',
  'bg-point-4',
  'bg-point-5',
  'bg-point-6',
  'bg-point-7',
  'bg-point-8',
  'bg-point-9',
  'bg-point-10',
  'bg-point-11',
  'bg-point-12',
  'bg-point-13',
  'bg-point-14',
  'bg-point-15',
  'bg-point-16',
  'bg-point-17',
  'bg-point-18',
  'bg-point-19',
  'bg-point-20',
  'bg-point-21',
  'bg-point-22',
  'bg-point-23',
  'bg-point-24',
] as const;

/** CSS grid placement for point `point`, matching the 3D board: the viewer sits
 *  at white's seat, so the NEAR row (screen bottom) carries 12…7 | bar | 6…1
 *  left-to-right and the FAR row carries 13…18 | bar | 19…24. Columns are
 *  1-based: 1–6 left points, 7 the bar, 8–13 right points, 14 the off tray.
 *  The same layout as `backgammonPointU`; a test walks all 24 points against it.
 *  (Far-right used to be `8 + (24 - point)`, which put 24 beside the bar.) */
export function pointGridCell(point: number): { row: 1 | 2; col: number } {
  if (point <= 6) return { row: 2, col: 14 - point };
  if (point <= 12) return { row: 2, col: 13 - point };
  if (point <= 18) return { row: 1, col: point - 12 };
  return { row: 1, col: point - 11 };
}

/** Column widths in WORLD units (point, bar, point, off tray), so each grid
 *  track is exactly as wide as the 3D thing it covers. The grid spans
 *  `BACKGAMMON_OVERLAY_U` and is warped onto the board plane by the projection
 *  the board reports (`BackgammonBoard`'s `onOverlayTransform`). */
export const BG_GRID_COLUMNS: readonly number[] = [
  ...Array.from({ length: 6 }, () => BACKGAMMON_POINT_WIDTH),
  BACKGAMMON_BAR_WIDTH,
  ...Array.from({ length: 6 }, () => BACKGAMMON_POINT_WIDTH),
  BACKGAMMON_OVERLAY_U.right - 6 * BACKGAMMON_POINT_WIDTH - BACKGAMMON_BAR_WIDTH / 2,
];

export interface BackgammonTapLayerProps {
  readonly position: BackgammonPosition;
  /** Tapped source awaiting a destination. */
  readonly selected: BackgammonEndpoint | null;
  /** Destinations reachable from `selected`. */
  readonly targets: ReadonlyArray<BackgammonEndpoint>;
  /** Whose checkers the human is — highlights and hitability key off it. */
  readonly playerColor: 'w' | 'b';
  readonly onTap: (endpoint: BackgammonEndpoint) => void;
  /** Checkers that can move now (assist option); mirrored as `data-movable`. */
  readonly movable?: ReadonlyArray<BackgammonEndpoint> | null;
  /** Destinations that would leave a blot exposed (assist option); drawn as a
   *  badge and mirrored as `data-blot` = `direct` | `indirect`. */
  readonly risks?: ReadonlyMap<BackgammonEndpoint, BlotRisk> | null;
  /** CSS `matrix3d` from the board's `onOverlayTransform`: warps this flat grid
   *  onto the perspective board. Undefined leaves the grid flat. */
  readonly transform?: string;
  /** Telemetry only (mirrors SquareOverlay): `data-board-ready` for the audit
   *  runner's first-paint wait. Input is never gated on it. */
  readonly ready: boolean;
}

/** The points a tap on `endpoint` would HIT, given the position and the
 *  mover — exactly one enemy checker, sent to the bar. Drives the red
 *  target styling, keeping mint = quiet move, red = capture in step with
 *  the chess board's visual language. */
function hitEndpoints(position: BackgammonPosition, playerColor: 'w' | 'b'): Set<number> {
  const out = new Set<number>();
  for (let point = 1; point <= 24; point++) {
    const c = position.points[point - 1] ?? 0;
    if (playerColor === 'w' ? c === -1 : c === 1) out.add(point);
  }
  return out;
}

export function BackgammonTapLayer({
  position,
  selected,
  targets,
  playerColor,
  onTap,
  ready,
  movable = null,
  risks = null,
  transform,
}: BackgammonTapLayerProps) {
  const movableSet = useMemo(() => new Set(movable ?? []), [movable]);
  const targetSet = useMemo(() => new Set(targets), [targets]);
  const hits = useMemo(() => hitEndpoints(position, playerColor), [position, playerColor]);
  const barCount = position.bar[playerColor];

  const cellClass = (isSelected: boolean, isTarget: boolean, isHit: boolean, isMovable = false) =>
    `relative m-px rounded-md transition-colors ${
      isSelected
        ? 'bg-sun/70 ring-2 ring-sun'
        : isTarget
          ? isHit
            ? 'bg-red-400/40'
            : 'bg-mint/40'
          : isMovable
            ? 'bg-sky/20 ring-2 ring-sky/70 hover:bg-sky/30'
            : 'hover:bg-white/10'
    }`;

  /** Warning badge for a destination that would leave a blot exposed. */
  const riskBadge = (risk: BlotRisk | undefined) =>
    risk ? (
      <span
        role="img"
        aria-label={
          risk === 'direct'
            ? 'warning: this checker could be hit by a single die'
            : 'caution: this checker could be hit by a combined roll'
        }
        // At the cell's vertical middle, which is where the checker stands (the cell
        // is the whole half-board row and the stack sits on its centre line). It used
        // to hug the bottom corner, a long way from the checker it warns about.
        className={`pointer-events-none absolute right-0.5 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center rounded-full text-[0.6875rem] font-black leading-none shadow ring-1 ring-ink/40 ${
          risk === 'direct' ? 'bg-amber-400 text-ink' : 'border-2 border-amber-400 bg-cream text-ink'
        }`}
      >
        {risk === 'direct' ? '!' : '?'}
      </span>
    ) : null;

  return (
    <div
      className="absolute inset-0 grid"
      style={{
        gridTemplateColumns: BG_GRID_COLUMNS.map((w) => `minmax(0, ${w}fr)`).join(' '),
        gridTemplateRows: "minmax(0, 1fr) minmax(0, 1fr)",
        ...(transform ? { transform, transformOrigin: '0 0' } : null),
      }}
      data-testid="bg-tap-layer"
      data-appdom-id="bg-tap-layer"
      data-appdom-type="Grid"
      // Telemetry, not appdom — see SquareOverlay for the reasoning.
      data-board-ready={ready ? 'true' : 'false'}
    >
      {/* Far-row points (13–24), then the bar, then near-row (1–12) — authored
          in point order via the shared cell table so the DOM order can never
          disagree with the grid placement. */}
      {Array.from({ length: 24 }, (_, i) => {
        const point = i + 1;
        const { row, col } = pointGridCell(point);
        const testid = BG_POINT_TESTIDS[i];
        if (!testid) return null;
        const isSelected = selected === point;
        const isTarget = targetSet.has(point);
        const isHit = isTarget && hits.has(point);
        return (
          <button
            key={testid}
            type="button"
            aria-label={`point ${point}`}
            onClick={() => onTap(point)}
            style={{ gridColumn: col, gridRow: row }}
            data-testid={testid}
            data-appdom-id={testid}
            data-appdom-type="Button"
            data-point={point}
            // Absent-when-unset, like data-shields: a query for [data-target]
            // should select only the squares that are actually legal now.
            {...(isSelected ? { 'data-selected': 'true' } : null)}
            {...(isTarget ? { 'data-target': 'true' } : null)}
            {...(isHit ? { 'data-hit': 'true' } : null)}
            {...(movableSet.has(point) ? { 'data-movable': 'true' } : null)}
            {...(risks?.has(point) ? { 'data-blot': risks.get(point) } : null)}
            className={cellClass(isSelected, isTarget, isHit, movableSet.has(point))}
          >
            {riskBadge(risks?.get(point))}
          </button>
        );
      })}
      <button
        type="button"
        aria-label="bar"
        onClick={() => onTap('bar')}
        style={{ gridColumn: 7, gridRow: '1 / 3' }}
        data-testid="bg-bar"
        data-appdom-id="bg-bar"
        data-appdom-type="Button"
        {...(movableSet.has('bar') ? { 'data-movable': 'true' } : null)}
        className={cellClass(selected === 'bar', targetSet.has('bar'), false, movableSet.has('bar'))}
      >
        {/* A visible cue that checkers are waiting to re-enter — the bar is
            otherwise empty sky in the middle of the board. */}
        {barCount > 0 && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-1/2 top-1/2 flex h-4 min-w-4 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-berry px-1 text-[0.5625rem] font-bold leading-none text-cream"
          >
            {barCount}
          </span>
        )}
      </button>
      <button
        type="button"
        aria-label="off tray"
        onClick={() => onTap('off')}
        style={{ gridColumn: 14, gridRow: '1 / 3' }}
        data-testid="bg-off"
        data-appdom-id="bg-off"
        data-appdom-type="Button"
        className={cellClass(selected === 'off', targetSet.has('off'), false)}
      />
    </div>
  );
}
